import * as React from 'react'
import { useSaveAppointment } from '@/lib/queries.js'
import { fromLocalInput, humanise, toLocalInput } from '@/lib/format.js'
import { fullName } from '@/lib/utils.js'
import { Button } from '@/components/ui/button.jsx'
import { Field, Label } from '@/components/ui/field.jsx'
import { Select } from '@/components/ui/select.jsx'
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog.jsx'
import { Alert } from '@/components/ui/feedback.jsx'
import { ERR, humanMessage } from '@/lib/errors.js'

const TYPES = [
  'consultation', 'estimate', 'follow_up', 'install', 'inspection',
  'walkthrough', 'delivery', 'custom'
]

/** Two hours from now, rounded to the next hour — the common case. */
function defaultWindow () {
  const start = new Date()
  start.setMinutes(0, 0, 0)
  start.setHours(start.getHours() + 2)
  const end = new Date(start)
  end.setHours(end.getHours() + 2)
  return { start: toLocalInput(start.toISOString()), end: toLocalInput(end.toISOString()) }
}

/**
 * Book an appointment.
 *
 * The end time auto-follows the start (+2h) until the user touches it, because
 * the API rejects an end before a start with 400 `invalid_window` and the most
 * common way to hit that is changing the start on a form where the end is
 * already filled in. Preventing the error beats explaining it.
 */
export function NewAppointmentDialog ({ open, onOpenChange, jobId, users = [] }) {
  const save = useSaveAppointment()
  const [form, setForm] = React.useState(() => {
    const w = defaultWindow()
    return { title: '', type: 'consultation', start: w.start, end: w.end, assigned_user_id: '' }
  })
  const [endTouched, setEndTouched] = React.useState(false)
  const [error, setError] = React.useState(null)

  React.useEffect(() => {
    if (open) {
      const w = defaultWindow()
      setForm({ title: '', type: 'consultation', start: w.start, end: w.end, assigned_user_id: '' })
      setEndTouched(false)
      setError(null)
    }
  }, [open])

  function setStart (value) {
    setForm(f => {
      if (endTouched) return { ...f, start: value }
      const start = new Date(value)
      if (Number.isNaN(start.getTime())) return { ...f, start: value }
      const end = new Date(start)
      end.setHours(end.getHours() + 2)
      return { ...f, start: value, end: toLocalInput(end.toISOString()) }
    })
  }

  async function handleSubmit (event) {
    event.preventDefault()
    setError(null)
    try {
      await save.mutateAsync({
        job_id: jobId,
        title: form.title.trim(),
        type: form.type,
        scheduled_start: fromLocalInput(form.start),
        scheduled_end: fromLocalInput(form.end),
        assigned_user_id: form.assigned_user_id || undefined
      })
      onOpenChange(false)
    } catch (err) {
      setError(err)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Schedule a visit">
        <form id="appt-form" onSubmit={handleSubmit} className="space-y-4" noValidate>
          {error && (
            <Alert tone="danger">
              {error.slug === ERR.INVALID_WINDOW
                ? 'The finish time has to be after the start time.'
                : humanMessage(error)}
            </Alert>
          )}

          <Field
            id="appt-title"
            label="What is it?"
            placeholder="Measure and quote"
            value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            required
          />

          <div className="space-y-1.5">
            <Label htmlFor="appt-type">Type</Label>
            <Select
              id="appt-type" value={form.type}
              onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
            >
              {TYPES.map(t => <option key={t} value={t}>{humanise(t)}</option>)}
            </Select>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              id="appt-start" label="Starts" type="datetime-local"
              value={form.start} onChange={e => setStart(e.target.value)} required
            />
            <Field
              id="appt-end" label="Finishes" type="datetime-local"
              value={form.end}
              onChange={e => { setEndTouched(true); setForm(f => ({ ...f, end: e.target.value })) }}
              required
            />
          </div>

          {users.length > 0 && (
            <div className="space-y-1.5">
              <Label htmlFor="appt-user">Who is going?</Label>
              <Select
                id="appt-user" value={form.assigned_user_id}
                onChange={e => setForm(f => ({ ...f, assigned_user_id: e.target.value }))}
              >
                <option value="">Nobody yet</option>
                {users.map(u => <option key={u.id} value={u.id}>{fullName(u)}</option>)}
              </Select>
            </div>
          )}
        </form>

        <DialogFooter className="-mx-5 -mb-4 mt-4">
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit" form="appt-form" loading={save.isPending} disabled={!form.title.trim()}>
            Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
